// Pure reconcile engine. No I/O, no side effects.
//
// Input:
//   parsedStatement  — { bank, summary, transactions[] }
//   cardTxns         — all Floosi transactions (unfiltered)
//   cardId           — the card being reconciled
//
// Output: ReconcileRow[] — a mix of 'statement' rows and 'floosi' rows:
//   { kind:'statement', parsedTxn, status:'new'|'matched'|'skip'|'payment_missing',
//     matchedTx, suggested, suggestReason }
//   { kind:'floosi',    floosiTx,  status:'floosi_only' }
//
// MATCHING MODEL — the merchant is the anchor.
//
// Three dimensions, direction (refund counts as credit) is a hard pre-filter:
//   • merchant — the FIRST 4+ letter word matches (prefix-tolerant). The city
//     and FX boilerplate follow the merchant, so they can't create a match.
//   • amount   — exact (±0.01) / close (≤15% FX drift) / far.
//   • date     — clean (purchase ≤3d, refund ≤10d) / drift (≤7d / ≤14d) / far.
//
//   • Merchant MATCHES → at least ONE of {exact amount, clean date} must be solid:
//       - amount exact AND clean date              → AUTO-match ('matched').
//       - one solid + the other within close/drift → SUGGEST (name what moved).
//       - both only close/drift, or either far     → not the same.
//   • Merchant does NOT match → only the one safe coincidence:
//       - amount exact AND clean date              → SUGGEST ('different name').
//       - anything looser                          → nothing (it's just 'new').
//
// So neither amount-proximity-alone nor a shared city/substring can fire a
// match. Suggestions are never auto-linked or consumed — the user confirms
// them. Auto-matches (merchant + exact amount + clean date) consume their tx.
//
// Payments ("…PAYMENT RECEIVED THANK YOU") are validated against the
// transfer-INTO-this-card pool (amount + window): matched → 'matched',
// unmatched → 'payment_missing'. 'islamic_net' → 'skip'. Floosi-only rows are
// clamped to the statement's date window.

const DAY_MS = 86_400_000;

function txDate(pt) {
  return pt.date || pt.postingDate || null;
}

function dayDiff(isoA, isoB) {
  return Math.abs(new Date(isoA + 'T00:00:00Z').getTime() - new Date(isoB + 'T00:00:00Z').getTime()) / DAY_MS;
}

// Two date bars, wider for refunds (merchants submit credits late):
//   windowDays = "clean" (auto-link eligible / counts as a solid leg)
//   driftDays  = outer "still worth suggesting" cap; beyond it = different tx.
// Purchases: clean ≤3, suggest ≤7. Refunds/credits: clean ≤10, suggest ≤14.
function windowDays(pt) {
  return pt.credit ? 10 : 3;
}
function driftDays(pt) {
  return pt.credit ? 14 : 7;
}

function dateDist(t, pt) {
  const pd = txDate(pt);
  return (pd && t.date) ? dayDiff(t.date, pd) : Infinity;
}

// A Floosi tx reduces card debt (matches a statement CREDIT) when it's income
// OR a refund (Floosi stores a refund as a negative expense, isRefund true).
function isCreditTx(t) {
  return t.type === 'income' || (t.type === 'expense' && !!t.isRefund);
}

const tokenize = (s) => String(s || '').toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
// Leading words that aren't the merchant (refund/reversal tags the user or SMS prefixes).
const QUALIFIERS = new Set(['REFUND', 'REVERSAL', 'RETURN', 'REFND', 'REIMBURSEMENT']);

// The merchant name LEADS both the SMS and the statement description; the city
// and FX boilerplate follow. So take the first 4+ letter word (skipping a
// refund-type qualifier). Short prefixes (M, H, AL, LE, S) fall through the
// 4-char floor automatically.
function firstMerchantWord(s) {
  return tokenize(s).find(t => t.length >= 4 && !QUALIFIERS.has(t)) || null;
}

// Merchant matches if those leading words are equal or one is a prefix of the
// other: GLOVO↔GLOVOPZ, FERRY↔FERRYSCANNER, WAIKIKI↔WAIKIKI, ALSHAYA↔ALSHAYA.
// A shared CITY (MOHAMEDIA) or substring coincidence (MEDIA inside MOHAMEDIA)
// can't match, because the city is never the first word.
function merchantMatch(a, b) {
  const wa = firstMerchantWord(a), wb = firstMerchantWord(b);
  if (!wa || !wb) return false;
  return wa === wb || wa.startsWith(wb) || wb.startsWith(wa);
}

// 2 = exact, 1 = within 15% (FX/hold drift), 0 = too far.
function tierOf(a, b) {
  const d = Math.abs(a - b);
  if (d <= 0.01) return 2;
  if (d / Math.max(Math.abs(b), 0.01) <= 0.15) return 1;
  return 0;
}

// Compare the AED (billed) amount AND — when both sides carry the same foreign
// currency — the ORIGINAL amount, then take the BEST of the two.
//   • AED alone can drift a few fils (the conversion is rounded at settlement),
//     so the foreign figure is often the tighter anchor (MAD 25 == MAD 25).
//   • But a stored original can be wrong (bad SMS/manual entry) while the AED is
//     spot-on — that must NOT veto an otherwise-perfect match. So neither side's
//     figure can drag the tier below what the other confirms; we never take the
//     worse of the two.
// Magnitudes only — direction is pre-filtered by isCreditTx, and Floosi may
// store a credit/refund as a signed value.
function amtTier(t, pt) {
  const aedTier = tierOf(Math.abs(Number(t.amount) || 0), Math.abs(Number(pt.amount) || 0));
  const toc = t.originalCurrency, poc = pt.originalCurrency;
  const toa = Math.abs(Number(t.originalAmount)), poa = Math.abs(Number(pt.originalAmount));
  const canOrig = toc && poc && toc === poc && Number.isFinite(toa) && Number.isFinite(poa) && poa > 0;
  const origTier = canOrig ? tierOf(toa, poa) : 0;
  return Math.max(aedTier, origTier);
}

// 2 = clean (in window), 1 = drifted but within the suggest cap, 0 = too far.
function dateTier(t, pt) {
  const dist = dateDist(t, pt);
  if (dist <= windowDays(pt)) return 2;
  if (dist <= driftDays(pt)) return 1;
  return 0;
}

// A candidate is viable only with a real anchor:
//  • merchant matches  → at least ONE of {exact amount, clean date} must be
//    solid; both-loose is too speculative (kills wrong same-merchant pairings).
//  • merchant doesn't  → only an EXACT amount on a clean date ("different name").
function viable(t, pt) {
  if (isCreditTx(t) !== !!pt.credit) return false;
  const a = amtTier(t, pt), d = dateTier(t, pt);
  if (a === 0 || d === 0) return false;
  if (merchantMatch(t.description, pt.description)) return a === 2 || d === 2;
  return a === 2 && d === 2;
}

function scoreCand(t, pt) {
  const m = merchantMatch(t.description, pt.description) ? 1 : 0;
  return m * 4 + amtTier(t, pt) * 2 + dateTier(t, pt);
}

// All three align: matching merchant + exact amount + in-window date.
function findAuto(pt, pool) {
  const cands = pool.filter(t =>
    isCreditTx(t) === !!pt.credit &&
    merchantMatch(t.description, pt.description) &&
    amtTier(t, pt) === 2 && dateTier(t, pt) === 2);
  cands.sort((a, b) => dateDist(a, pt) - dateDist(b, pt));
  return cands[0] || null;
}

// Best viable-but-not-auto candidate, with the reason it isn't a clean match.
function findSuggest(pt, pool) {
  const cands = pool.filter(t => viable(t, pt));
  if (!cands.length) return null;
  cands.sort((a, b) =>
    scoreCand(b, pt) - scoreCand(a, pt) ||
    dateDist(a, pt) - dateDist(b, pt) ||
    Math.abs((Number(a.amount) || 0) - pt.amount) - Math.abs((Number(b.amount) || 0) - pt.amount));
  const tx = cands[0];
  const meta = {
    merchant: merchantMatch(tx.description, pt.description),
    amount: amtTier(tx, pt),
    date: dateTier(tx, pt),
  };
  const parts = [];
  if (!meta.merchant) parts.push('merchant');
  if (meta.amount < 2) parts.push('amount');
  if (meta.date < 2) parts.push('date');
  return { tx, reason: parts.join('+'), meta };
}

// Card-bill payment: a transfer INTO this card of the same amount, within window.
function findPayment(pt, transferPool) {
  if (!txDate(pt)) return null;
  const cands = transferPool.filter(t =>
    Math.abs((Number(t.amount) || 0) - pt.amount) <= 0.01 &&
    dateDist(t, pt) <= windowDays(pt));
  cands.sort((a, b) => dateDist(a, pt) - dateDist(b, pt));
  return cands[0] || null;
}

export function reconcile({ parsedStatement, cardTxns, cardId }) {
  const live = cardTxns.filter(t => !t.deletedAt);

  // Pool: active, non-transfer, unlinked txns for this card
  const pool = live.filter(t =>
    (t.cardId === cardId || t.card_id === cardId) &&
    t.type !== 'transfer' &&
    !t.linkedStatementId
  );

  // Transfers landing ON this card — where card-bill payments live in Floosi.
  const transferPool = live.filter(t =>
    t.type === 'transfer' &&
    (t.toCardId === cardId || t.to_card_id === cardId) &&
    !t.linkedStatementId
  );

  const usedIds = new Set();
  const usedTransferIds = new Set();
  const statementRows = [];
  const suggestQueue = []; // rows with no confident auto-match

  // Pass 1 — confident auto-match (all three align), plus payment validation.
  for (const pt of parsedStatement.transactions) {
    if (pt.tag === 'islamic_net') {
      statementRows.push({ kind: 'statement', parsedTxn: pt, status: 'skip', matchedTx: null });
      continue;
    }
    if (pt.tag === 'payment') {
      const xfer = findPayment(pt, transferPool.filter(t => !usedTransferIds.has(t.id)));
      if (xfer) {
        usedTransferIds.add(xfer.id);
        statementRows.push({ kind: 'statement', parsedTxn: pt, status: 'matched', matchedTx: xfer });
      } else {
        statementRows.push({ kind: 'statement', parsedTxn: pt, status: 'payment_missing', matchedTx: null });
      }
      continue;
    }
    const auto = findAuto(pt, pool.filter(t => !usedIds.has(t.id)));
    if (auto) {
      usedIds.add(auto.id);
      statementRows.push({ kind: 'statement', parsedTxn: pt, status: 'matched', matchedTx: auto });
    } else {
      suggestQueue.push(pt);
    }
  }

  // Pass 2 — suggestions (never auto-linked, never consumed). The candidate
  // stays available as a Floosi-only row / for the user to confirm.
  for (const pt of suggestQueue) {
    const sug = findSuggest(pt, pool.filter(t => !usedIds.has(t.id)));
    statementRows.push({
      kind: 'statement', parsedTxn: pt, status: 'new', matchedTx: null,
      suggested: sug ? sug.tx : null,
      suggestReason: sug ? sug.reason : null,
      suggestMeta: sug ? sug.meta : null,
    });
  }

  // Statement date window: only Floosi txs inside the cycle the statement
  // covers can be "missing from the statement".
  const stmtDates = parsedStatement.transactions.map(txDate).filter(Boolean).sort();
  const periodStart = stmtDates[0] || null;
  const periodEnd = parsedStatement.summary?.statementDate || stmtDates[stmtDates.length - 1] || null;
  const inWindow = (t) =>
    (!periodStart || (t.date && t.date >= periodStart)) &&
    (!periodEnd || (t.date && t.date <= periodEnd));

  const floosiRows = pool
    .filter(t => !usedIds.has(t.id) && inWindow(t))
    .map(t => ({ kind: 'floosi', floosiTx: t, status: 'floosi_only' }));

  return [...statementRows, ...floosiRows];
}
